export type WaterfallListSession = {
  sessionId: string;
  lastActivity: string;
  title?: string | null;
  peerKey?: string | null;
  endedAt?: string | null;
};

/** 瀑布清單生命週期篩選。 */
export type LifeFilter = 'all' | 'alive' | 'ended';

export function sessionEnded(session: { endedAt?: string | null }): boolean {
  return (session.endedAt?.trim() ?? '') !== '';
}

export function filterSessionsByLife<T extends { endedAt?: string | null }>(
  sessions: readonly T[],
  filter: LifeFilter,
): T[] {
  if (filter === 'all') return [...sessions];
  if (filter === 'alive') return sessions.filter((row) => !sessionEnded(row));
  return sessions.filter((row) => sessionEnded(row));
}

export type SessionHeading = {
  primary: string;
  peerKey: string | null;
};

export type HumanSendPlan = 'confirm' | 'send';

export type TakeoverArm = 'none' | 'leave-auto' | 'return-auto';

export type ComposerKeyAction = 'send' | 'newline' | 'hold' | 'pass';

export type ComposerKey = {
  key: string;
  shiftKey: boolean;
  altKey: boolean;
  ctrlKey: boolean;
  metaKey: boolean;
  isComposing: boolean;
  repeat: boolean;
};

function activityMs(value: string): number | null {
  const ms = Date.parse(value);
  return Number.isFinite(ms) ? ms : null;
}

function bySessionId(a: string, b: string): number {
  if (a < b) return -1;
  if (a > b) return 1;
  return 0;
}

export function sortSessionsByLastActivity<T extends WaterfallListSession>(sessions: readonly T[]): T[] {
  return [...sessions].sort((a, b) => {
    const left = activityMs(a.lastActivity);
    const right = activityMs(b.lastActivity);
    if (left === null && right === null) return bySessionId(a.sessionId, b.sessionId);
    if (left === null) return 1;
    if (right === null) return -1;
    if (left !== right) return right - left;
    return bySessionId(a.sessionId, b.sessionId);
  });
}

function trimmed(value: string | null | undefined): string {
  return value?.trim() ?? '';
}

export function sessionHeading(session: WaterfallListSession): SessionHeading {
  const title = trimmed(session.title);
  const peerKey = trimmed(session.peerKey);
  return {
    primary: title || session.sessionId,
    peerKey: peerKey || null,
  };
}

export function planHumanSend(mode: 'auto' | 'human', confirmed: boolean): HumanSendPlan {
  if (mode === 'human') return 'send';
  if (confirmed) return 'send';
  return 'confirm';
}

export function dismissTakeoverArm(arm: TakeoverArm, key: string): TakeoverArm {
  if (key === 'Escape') return 'none';
  return arm;
}

export function headerModeClick(mode: 'auto' | 'human'): 'to-human' | 'ask-auto' {
  if (mode === 'auto') return 'to-human';
  return 'ask-auto';
}

export function confirmReturnAuto(mode: 'auto' | 'human', arm: TakeoverArm): boolean {
  return mode === 'human' && arm === 'return-auto';
}

export function sayErrorBesideComposer(status: number): boolean {
  return status === 409 || status === 403;
}

export function assigneeText(value: string | null | undefined): string | null {
  const text = value?.trim() ?? '';
  if (text === '') return null;
  return text;
}

export function markSessionMode<T extends { sessionId: string; mode: 'auto' | 'human' }>(
  sessions: readonly T[],
  sessionId: string,
  mode: 'auto' | 'human',
): T[] {
  let changed = false;
  const next = sessions.map((session) => {
    if (session.sessionId !== sessionId || session.mode === mode) return session;
    changed = true;
    return { ...session, mode };
  });
  return changed ? next : (sessions as T[]);
}

export function mergeSessionsById<T extends { sessionId: string; mode: 'auto' | 'human' }>(
  local: readonly T[],
  incoming: readonly T[],
  incomingTicket: number,
  heldAt: Readonly<Record<string, number>>,
): T[] {
  const localMode = new Map(local.map((session) => [session.sessionId, session.mode]));
  return incoming.map((row) => {
    const held = heldAt[row.sessionId] ?? 0;
    if (held > incomingTicket && localMode.get(row.sessionId) === 'human' && row.mode !== 'human') {
      return { ...row, mode: 'human' as const };
    }
    return row;
  });
}

const INBOX_DIGIT = /^[1-9]$/;

export function composerKeyAction(event: ComposerKey): ComposerKeyAction {
  if (event.isComposing) return 'pass';
  const withModifier = event.altKey || event.ctrlKey || event.metaKey;
  if (INBOX_DIGIT.test(event.key) && withModifier) return 'hold';
  if (event.key === 'Enter' && event.shiftKey) return 'newline';
  if (event.key === 'Enter' && event.repeat) return 'hold';
  if (event.key === 'Enter') return 'send';
  return 'pass';
}

export type PreviewMessage = {
  sessionId: string;
  body: string;
  ts: string;
  direction?: string;
};

export type DraftMap = Readonly<Record<string, string>>;

export type ListKeyMove = { kind: 'pass' } | { kind: 'select'; sessionId: string | null };

const MINUTE_MS = 60_000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

function lastBySession<T extends { sessionId: string; ts: string }>(messages: readonly T[]): Map<string, T> {
  const best = new Map<string, { index: number; row: T }>();
  messages.forEach((row, index) => {
    const current = best.get(row.sessionId);
    if (!current || row.ts > current.row.ts || (row.ts === current.row.ts && index >= current.index)) {
      best.set(row.sessionId, { index, row });
    }
  });
  const out = new Map<string, T>();
  for (const [sessionId, entry] of best) out.set(sessionId, entry.row);
  return out;
}

export function lastBodyBySession(messages: readonly PreviewMessage[]): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [sessionId, row] of lastBySession(messages)) out[sessionId] = row.body.trim();
  return out;
}

export function mergeLastBodies(
  prev: Readonly<Record<string, string>>,
  messages: readonly PreviewMessage[],
): Record<string, string> {
  if (messages.length === 0) return prev;
  return { ...prev, ...lastBodyBySession(messages) };
}

export function draftText(drafts: DraftMap, sessionId: string | null): string {
  if (!sessionId) return '';
  return drafts[sessionId] ?? '';
}

export function rememberDraft(drafts: DraftMap, sessionId: string, text: string): DraftMap {
  if (drafts[sessionId] === text) return drafts;
  return { ...drafts, [sessionId]: text };
}

export function clearDraft(drafts: DraftMap, sessionId: string): DraftMap {
  if (!(sessionId in drafts)) return drafts;
  const next = { ...drafts };
  delete next[sessionId];
  return next;
}

export function defaultSessionId(
  sessions: readonly WaterfallListSession[],
  messages: readonly PreviewMessage[],
): string | null {
  const ordered = sortSessionsByLastActivity(sessions);
  const newest = ordered[0];
  if (!newest) return null;
  const last = lastBySession(messages);
  const needy = ordered.find((session) => last.get(session.sessionId)?.direction === 'in');
  return (needy ?? newest).sessionId;
}

export function listKeyMove(sessionIds: readonly string[], activeId: string | null, key: string): ListKeyMove {
  if (key !== 'j' && key !== 'k') return { kind: 'pass' };
  const row: (string | null)[] = [null, ...sessionIds];
  const found = row.indexOf(activeId);
  const index = found < 0 ? 0 : found;
  const next = key === 'j' ? Math.min(row.length - 1, index + 1) : Math.max(0, index - 1);
  return { kind: 'select', sessionId: row[next] ?? null };
}

export type DirectedMessage = {
  id: string;
  sessionId: string;
  ts: string;
  direction?: string;
};

export type ReplyCursor = {
  inboundAfterOutbound: number;
  latestInboundTs: string | null;
  needsReply: boolean;
};

export function mergeDirected<T extends { id: string }>(prev: readonly T[], incoming: readonly T[]): T[] {
  if (incoming.length === 0) return [...prev];
  const map = new Map<string, T>();
  for (const row of prev) map.set(row.id, row);
  for (const row of incoming) map.set(row.id, row);
  return [...map.values()];
}

export function replyCursor(messages: readonly DirectedMessage[]): ReplyCursor {
  const ordered = messages.map((row, index) => ({ row, index })).sort((a, b) => {
    if (a.row.ts === b.row.ts) return a.index - b.index;
    return a.row.ts < b.row.ts ? -1 : 1;
  });
  let lastOut = -1;
  ordered.forEach(({ row }, index) => {
    if (row.direction === 'out') lastOut = index;
  });
  let inboundAfterOutbound = 0;
  let latestInboundTs: string | null = null;
  ordered.forEach(({ row }, index) => {
    if (row.direction !== 'in') return;
    latestInboundTs = row.ts;
    if (index > lastOut) inboundAfterOutbound += 1;
  });
  return {
    inboundAfterOutbound,
    latestInboundTs,
    needsReply: inboundAfterOutbound > 0,
  };
}

export function repliesBySession(messages: readonly DirectedMessage[]): Map<string, ReplyCursor> {
  const groups = new Map<string, DirectedMessage[]>();
  for (const row of messages) {
    const list = groups.get(row.sessionId);
    if (list) list.push(row);
    else groups.set(row.sessionId, [row]);
  }
  const out = new Map<string, ReplyCursor>();
  for (const [sessionId, rows] of groups) out.set(sessionId, replyCursor(rows));
  return out;
}

export function sortSessionsByNeedsReply<T extends WaterfallListSession>(
  sessions: readonly T[],
  needsReplyIds: ReadonlySet<string>,
): T[] {
  const ranked = sortSessionsByLastActivity(sessions);
  const aliveNeedy: T[] = [];
  const aliveQuiet: T[] = [];
  const endedNeedy: T[] = [];
  const endedQuiet: T[] = [];
  for (const session of ranked) {
    const ended = sessionEnded(session);
    const needy = needsReplyIds.has(session.sessionId);
    if (!ended && needy) aliveNeedy.push(session);
    else if (!ended) aliveQuiet.push(session);
    else if (needy) endedNeedy.push(session);
    else endedQuiet.push(session);
  }
  return [...aliveNeedy, ...aliveQuiet, ...endedNeedy, ...endedQuiet];
}

export function nextNeedsReplyId(
  orderedIds: readonly string[],
  needsReplyIds: ReadonlySet<string>,
  activeId: string | null,
): string | null {
  if (orderedIds.length === 0) return null;
  const start = activeId === null ? -1 : orderedIds.indexOf(activeId);
  for (let step = 1; step <= orderedIds.length; step += 1) {
    const id = orderedIds[(start + step) % orderedIds.length];
    if (!id || id === activeId) continue;
    if (needsReplyIds.has(id)) return id;
  }
  return null;
}

export function replyDotVisible(needsReply: boolean, latestInboundTs: string | null, seenTs: string | null): boolean {
  if (!needsReply || !latestInboundTs) return false;
  if (seenTs === null || seenTs === '') return true;
  return seenTs < latestInboundTs;
}

export const PENDING_REPLY_CHIP = '待回覆';

export function pendingReplyCount(sessionIds: readonly string[], needsReplyIds: ReadonlySet<string>): number {
  let count = 0;
  for (const id of sessionIds) {
    if (needsReplyIds.has(id)) count += 1;
  }
  return count;
}

export function pendingReplyBanner(count: number): string | null {
  if (count < 1) return null;
  return `${count} 則待回覆`;
}

export function formatRelativeZhTw(iso: string, nowMs: number): string {
  const ms = Date.parse(iso);
  if (!Number.isFinite(ms)) return '';
  const delta = nowMs - ms;
  if (delta >= 0 && delta < 45_000) return '剛剛';
  if (delta >= 45_000 && delta < HOUR_MS) return `${Math.max(1, Math.round(delta / MINUTE_MS))} 分鐘前`;
  if (delta >= HOUR_MS && delta < DAY_MS) return `${Math.max(1, Math.round(delta / HOUR_MS))} 小時前`;
  return new Intl.DateTimeFormat('zh-TW', {
    month: 'numeric',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).format(ms);
}
